import {
  IconAlertCircle,
  IconArchive,
  IconBook,
  IconChartDots,
  IconCheckbox,
  IconClipboardList,
  IconHexagonLetterA,
  // IconChartArrowsVertical,
  IconHome2,
  IconMessages,
  IconSettings,
  IconShare,
  // IconTarget,
  IconUsersGroup,
  IconMapSearch,
} from "@tabler/icons-react";

import { NavTab } from "~/utils/cookie";
import { getRouteWithCurrentHubId } from "~/utils/hubid";

export type ActionHubNavItem = {
  /**
   * Display title of the navigation item
   */
  title: string;

  /**
   * URL path for the navigation item (use getRouteWithCurrentHubId)
   */
  link: string;

  /**
   * Icon component from Tabler Icons to display with the navigation item
   */
  icon: React.FC<any>;

  /**
   * Whether this item can be used while logged out
   */
  checkPermission: boolean;

  /**
   * Optional identifier for tour functionality targeting this navigation item
   */
  tourTarget?: string;

  /**
   * URL segment used to determine if this navigation item should be highlighted,
   * using a startsWith check (after HubId, don't include that here)
   */
  highlightSegment: string | RegExp;
};

export type NavSectionCategory = NavTab | "always";

export type ActionHubNavSection = {
  /**
   * Section title to display as a header
   */
  sectionTitle: string;

  /**
   * Navigation items in this section
   */
  items: ActionHubNavItem[];

  /**
   * Category for tab filtering: "tracking", "training", "comms", or "always"
   * Sections with "always" are shown in all tabs
   */
  category: NavSectionCategory;

  /**
   * Optional badge to display next to the section title (e.g., "Beta", "New")
   */
  badge?: string;
};

export const NAV_SECTIONS = (
  currentHubId: string,
  hasRtmdxUsers: boolean = false,
  canEditCourses: boolean = false,
  learningCenterEnabled: boolean = false,
  canManageCrimeData: boolean = false,
  crimeDataEnabled: boolean = true,
  cardiffEnabled: boolean = false,
  cardiffIsAdmin: boolean = false,
  trackingEnabled: boolean = true,
  commsEnabled: boolean = true,
): ActionHubNavSection[] => {
  const sections: ActionHubNavSection[] = [
    {
      sectionTitle: "Overview",
      category: "always",
      items: [
        {
          title: "Home",
          link: getRouteWithCurrentHubId({
            currentHubId,
            route: "home",
            withoutPrefixBar: true,
          }),
          icon: IconHome2,
          checkPermission: false,
          tourTarget: "home",
          highlightSegment: "home",
        },
      ],
    },
    ...(trackingEnabled
      ? ([
          {
            sectionTitle: "Action Planning",
            category: "tracking",
            items: [
              {
                title: "Dashboard",
                link: getRouteWithCurrentHubId({
                  currentHubId,
                  route: "/dashboard",
                  withoutPrefixBar: true,
                }),
                icon: IconChartDots,
                checkPermission: false,
                tourTarget: "dashboard",
                highlightSegment: "dashboard",
              },
              {
                title: "Tasks",
                link: getRouteWithCurrentHubId({
                  currentHubId,
                  route: "tasks",
                  withoutPrefixBar: true,
                }),
                icon: IconCheckbox,
                checkPermission: true,
                tourTarget: "tasks",
                highlightSegment: "tasks",
              },
              {
                title: "Problems",
                link: getRouteWithCurrentHubId({
                  currentHubId,
                  route: "action-plans/problems",
                  withoutPrefixBar: true,
                }),
                icon: IconAlertCircle,
                checkPermission: true,
                tourTarget: "problems",
                highlightSegment: "action-plans/problems",
              },
              {
                title: "Actions",
                link: getRouteWithCurrentHubId({
                  currentHubId,
                  route: "action-plans/items",
                  withoutPrefixBar: true,
                }),
                icon: IconHexagonLetterA,
                checkPermission: true,
                tourTarget: "actions",
                highlightSegment: "action-plans/items",
              },
              {
                title: "Locator",
                link: getRouteWithCurrentHubId({
                  currentHubId,
                  route: "locator",
                  withoutPrefixBar: true,
                }),
                icon: IconMapSearch,
                checkPermission: true,
                tourTarget: "locator",
                highlightSegment: "locator",
              },
            ],
          },
          ...(crimeDataEnabled
            ? ([
                {
                  sectionTitle: "Crime Data",
                  category: "tracking",
                  badge: "Beta",
                  items: [
                    {
                      title: "Map View",
                      link: getRouteWithCurrentHubId({
                        currentHubId,
                        route: "crime-data/map",
                        withoutPrefixBar: true,
                      }),
                      icon: IconMapSearch,
                      checkPermission: false,
                      highlightSegment: /crime-data\/map/,
                    },
                    ...(canManageCrimeData
                      ? [
                          {
                            title: "Admin",
                            link: getRouteWithCurrentHubId({
                              currentHubId,
                              route: "crime-data/admin",
                              withoutPrefixBar: true,
                            }),
                            icon: IconSettings,
                            checkPermission: true,
                            highlightSegment: "crime-data/admin",
                          },
                        ]
                      : []),
                  ],
                },
              ] as ActionHubNavSection[])
            : []),
          {
            sectionTitle: "Resources",
            category: "tracking",
            items: [
              // POI Resource Groups are intentionally hidden from nav for now.
              // The route still exists at resource-inventory/groups for follow-up work.
              {
                title: "POI Resources",
                link: getRouteWithCurrentHubId({
                  currentHubId,
                  route: "resource-inventory/items",
                  withoutPrefixBar: true,
                }),
                icon: IconArchive,
                checkPermission: true,
                tourTarget: "resource",
                highlightSegment: "resource-inventory/items",
              },
              {
                title: "Services Inventory",
                link: getRouteWithCurrentHubId({
                  currentHubId,
                  route: "resources/services",
                  withoutPrefixBar: true,
                }),
                icon: IconUsersGroup,
                checkPermission: true,
                highlightSegment: "resources/services",
              },
            ],
          },
        ] as ActionHubNavSection[])
      : []),
    ...(commsEnabled
      ? ([
          {
            sectionTitle: "Collaborate",
            category: "comms",
            items: [
              {
                title: "Discussions",
                link: getRouteWithCurrentHubId({
                  currentHubId,
                  route: "collaborate/discussions",
                  withoutPrefixBar: true,
                }),
                icon: IconMessages,
                checkPermission: true,
                highlightSegment: "collaborate",
              },
              {
                title: "Sharing",
                link: getRouteWithCurrentHubId({
                  currentHubId,
                  route: "sharing",
                  withoutPrefixBar: true,
                }),
                icon: IconShare,
                checkPermission: true,
                tourTarget: "sharing",
                highlightSegment: "sharing",
              },
            ],
          },
        ] as ActionHubNavSection[])
      : []),
    ...(learningCenterEnabled
      ? [
          {
            sectionTitle: "Learning Center",
            category: "training",
            items: [
              {
                title: "Library",
                link: getRouteWithCurrentHubId({
                  currentHubId,
                  route: "learning-center/library",
                  withoutPrefixBar: true,
                }),
                icon: IconBook,
                checkPermission: false,
                tourTarget: "learning-library",
                highlightSegment: /learning-center\/(library|courses)/,
              },
              ...(canEditCourses
                ? [
                    {
                      title: "Admin",
                      link: getRouteWithCurrentHubId({
                        currentHubId,
                        route: "learning-center/admin",
                        withoutPrefixBar: true,
                      }),
                      icon: IconSettings,
                      checkPermission: true,
                      tourTarget: "learning-admin",
                      highlightSegment: "learning-center/admin",
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(cardiffEnabled
      ? ([
          {
            sectionTitle: "Cardiff Intake",
            category: "cardiff",
            items: [
              {
                title: "Intake",
                link: getRouteWithCurrentHubId({
                  currentHubId,
                  route: "cardiff",
                  withoutPrefixBar: true,
                }),
                icon: IconClipboardList,
                checkPermission: false,
                highlightSegment: /\/cardiff(\/intake|$)/,
              },
              ...(cardiffIsAdmin
                ? [
                    {
                      title: "Dashboard",
                      link: getRouteWithCurrentHubId({
                        currentHubId,
                        route: "cardiff/dashboard",
                        withoutPrefixBar: true,
                      }),
                      icon: IconChartDots,
                      checkPermission: true,
                      highlightSegment: "cardiff/dashboard",
                    },
                  ]
                : []),
            ],
          },
        ] as ActionHubNavSection[])
      : []),
  ];

  // Conditionally add Analytics section if any users in the hub have the rtmdx role
  if (hasRtmdxUsers) {
    sections.push({
      sectionTitle: "Analytics",
      category: "tracking",
      items: [
        {
          title: "RTMDx",
          link: getRouteWithCurrentHubId({
            currentHubId,
            route: "rtmdx",
            withoutPrefixBar: true,
          }),
          icon: IconChartDots,
          checkPermission: true,
          tourTarget: "rtmdx",
          highlightSegment: "rtmdx",
        },
      ],
    });
  }

  // Add Administration section last (visible in all tabs)
  sections.push({
    sectionTitle: "Administration",
    category: "always",
    items: [
      {
        title: "Manage Users",
        link: getRouteWithCurrentHubId({
          currentHubId,
          route: "teamwork/dashboard",
          withoutPrefixBar: true,
        }),
        icon: IconUsersGroup,
        checkPermission: true,
        tourTarget: "teamwork",
        highlightSegment: "teamwork",
      },
    ],
  });

  return sections;
};

// Legacy function for backward compatibility - flattens sections into a single array
export const NAV_ITEMS = (
  currentHubId: string,
  hasRtmdxUsers: boolean = false,
  canEditCourses: boolean = false,
  learningCenterEnabled: boolean = false,
  canManageCrimeData: boolean = false,
  crimeDataEnabled: boolean = true,
  cardiffEnabled: boolean = false,
  cardiffIsAdmin: boolean = false,
  trackingEnabled: boolean = true,
  commsEnabled: boolean = true,
): ActionHubNavItem[] => {
  return NAV_SECTIONS(
    currentHubId,
    hasRtmdxUsers,
    canEditCourses,
    learningCenterEnabled,
    canManageCrimeData,
    crimeDataEnabled,
    cardiffEnabled,
    cardiffIsAdmin,
    trackingEnabled,
    commsEnabled,
  ).flatMap((section) => section.items);
};
